import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from '@fluentui/react-components';
import { NOTIFICATION_TOASTER_ID } from './hooks/useNotificationToast';
import { ThemeProvider, ThemedFluentProvider } from './theme/ThemeContext';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { msalConfig } from './config/authConfig';
import App from './App';
import { publishBuildInfo } from './buildInfo';
import i18n from './i18n';
import './index.css';

// Publish the build stamp before anything else can fail.
//
// ⚠ THIS CALL IS LOAD-BEARING AND MUST NOT BE "TIDIED AWAY". The stamp reaches the shipped bundle
// only because something references it: on the first build of this feature the bundle hash did not
// change at all, and reading the artefact showed the stamp literal was ABSENT — rollup had
// tree-shaken the whole module because nothing imported it. A stamp nothing imports is a stamp
// that does not ship. `npm run build` now ends with `scripts/verify-build-stamp.mjs`, which reads
// the built artefact and FAILS THE BUILD if the literal is missing, so this cannot silently
// regress. (It is a build step rather than a test because CI runs `Test` before `Build`, so a
// dist-reading test would skip in CI every time.)
publishBuildInfo();

// Initialize MSAL - exported for use in API service
export const msalInstance = new PublicClientApplication(msalConfig);

// Default to using the first account if no account is active on page load
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
}

// Listen for sign-in events and set active account
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    msalInstance.setActiveAccount(payload.account);
  }
});

// Initialize React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* Explicit, not implicit: every bare useTranslation() in the staff app resolves via this
        context BEFORE react-i18next's module-level global fallback (getI18n()) -- see
        portalI18n.ts for how that global was clobbered when a second instance also called
        .use(initReactI18next). An explicit provider here means a future third instance doing
        the same thing can no longer break staff components, regardless of module load order. */}
    <I18nextProvider i18n={i18n}>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <ThemedFluentProvider>
              <Toaster toasterId={NOTIFICATION_TOASTER_ID} position="top-end" />
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemedFluentProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>
    </I18nextProvider>
  </React.StrictMode>
);
